import { useStateTransition } from "@codesandbox/states";
import { useEnvironmentInterface } from "environment-interface";
import type { AssetPaths } from "environment-interface/monacoEditor";
import type { LoadLibraryOpts } from "environment-interface/monacoEditor/browser/services/editor/vscode/monaco";
import { useAddAlertNotification } from "features/notifications/hooks";
import { useCsbApi } from "hooks/api/useCsbApi";
import { useEffect, useReducer } from "react";

import { createHookContext } from "utils/createHookContext";

import { usePitcher } from "../utils/usePitcher";

import { reducer } from "./reducer";

interface FeatureProps {
  assetPaths: AssetPaths;
  initialTheme: string;
  vscodeLoaderOpts: LoadLibraryOpts;
}

export const useMonacoLibrary = createHookContext((props: FeatureProps) => {
  const { monacoEditor } = useEnvironmentInterface();
  const pitcher = usePitcher();
  const addAlertNotification = useAddAlertNotification();
  const csbApi = useCsbApi();

  const editorReducer = useReducer(reducer, {
    state: "LOADING",
  });

  const [featureState, dispatch] = editorReducer;

  useEffect(() => monacoEditor.events.subscribe(dispatch), []);

  const loadMonaco = () => {
    monacoEditor.loadLibrary({
      initialTheme: props.initialTheme,
      assetPaths: props.assetPaths,
      vscodeLoaderOpts: props.vscodeLoaderOpts,
      api: csbApi,
      pitcher,
      showResetFileNotification: (id) => {
        const filePath = pitcher.clients.file.getPathFromFileId(id);
        return new Promise<"client" | "server" | void>((resolve) => {
          addAlertNotification({
            message: `The file ${
              filePath ? filePath.split("/").pop() : "N/A"
            } is out of sync. Which document version should we resolve with?`,
            actions: [
              {
                text: "My Version",
                onAction: () => resolve("client"),
              },
              {
                text: "Server Version",
                onAction: () => resolve("server"),
              },
            ],
            callback: () => resolve(),
          });
        });
      },
      showNotification(message, _type, actions) {
        return new Promise((resolve) => {
          addAlertNotification({
            message,
            // TODO: add support for type in the notification, as the LSP shows this
            // type,
            actions: actions?.map((action) => ({
              text: action.title,
              onAction: () => {
                resolve(action.title);
              },
            })),
            callback: () => resolve(null),
          });
        });
      },
    });
  };

  useStateTransition(featureState, "LOADING", loadMonaco, [pitcher]);

  /*
  We should not do this on reconnect. We need to implement two different continuations. From the same session,
  where we do not reinstantiate things, or from a different session, where we reinstantiate things
  useEffect(() => {
    const disposable = pitcher.onReconnected(loadMonaco);

    return () => {
      disposable.dispose();
    };
  }, [pitcher]);
  */

  return editorReducer;
});

export const MonacoLibraryFeature = useMonacoLibrary.Provider;
